"use client";
import * as React from "react";
import { Spinner } from "./Spinner.js";
import { cn } from "../private/utils.js";
import { cva, VariantProps } from "class-variance-authority";

interface ButtonProps extends React.ComponentPropsWithRef<"button"> {
  children: React.ReactNode;
  size?: "xs" | "small" | "medium" | "large";
  elevation?: "none" | "low" | "raised" | "floating" | "selected";
  animateSelected?: boolean;
  // rounded?: true;
  action?:
    | "primary"
    | "secondary"
    | "tertiary"
    | "cta"
    | "caution"
    | "warn"
    | undefined;
  loading?: boolean;
  className?: string;
  usePrimaryColors?: boolean;
  innerClassName?: string;
}

type ActionTypeProps = {
  usePrimaryColors?: string;
  default: string;
};
type AvailableActions =
  | "primary"
  | "secondary"
  | "tertiary"
  | "cta"
  | "caution"
  | "warn";
type ActionProps = {
  [key in AvailableActions]: ActionTypeProps;
};
export const buttonActions: ActionProps = {
  primary: {
    usePrimaryColors:
      " bg-primary-500 hover:bg-primary-600 text-primary-50 dark:bg-primary-700 dark:hover:bg-primary-800 dark:shadow-transparent shadow-primary-900/20",
    default:
      " dark:border-mono-700/50 bg-mono-500  text-mono-50 dark:bg-mono-700 dark:hover:border-mono-500 dark:shadow-transparent shadow-mono-900/20 border"
  },
  secondary: {
    usePrimaryColors:
      " bg-primary-100 hover:bg-primary-200 text-primary-800 dark:border dark:border-primary-500 dark:text-primary-200 dark:bg-transparent dark:hover:bg-primary-800/30 dark:shadow-transparent shadow-primary-800/20 dark:hover:border-primary-400",
    default:
      " bg-white border-transparent hover:border-mono-400 border-mono-300 text-mono-800 border border-border dark:text-mono-200 dark:bg-mono-800/50 dark:hover:bg-mono-800/50 dark:shadow-transparent shadow-mono-800/20 dark:hover:border-mono-700"
  },
  tertiary: {
    usePrimaryColors:
      "  hover:bg-mono-100 text-mono-800 dark:text-mono-200 dark:hover:bg-mono-700/20 border-transparent dark:shadow-transparent shadow-transparent",
    default:
      "  hover:bg-mono-100 text-mono-800 dark:text-mono-200 dark:hover:bg-mono-700/20 border-transparent dark:shadow-transparent shadow-transparent border"
  },
  cta: {
    default:
      " bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border border-transparent dark:shadow-transparent shadow-emerald-800/20 dark:bg-emerald-500 dark:text-emerald-50 dark:hover:bg-emerald-600"
  },
  caution: {
    default:
      " bg-red-100  hover:bg-red-200 text-red-800 border border-red-300/50 dark:border-red-300/50 dark:shadow-transparent shadow-red-800/20 dark:bg-red-500 dark:text-red-50 dark:hover:bg-red-600"
  },
  warn: {
    default:
      " bg-yellow-100 hover:bg-yellow-200 text-yellow-700 border border-transparent dark:shadow-transparent shadow-yellow-800/20 dark:bg-yellow-500 dark:text-yellow-50 dark:hover:bg-yellow-600"
  }
};

const sizes = {
  xs: " px-2 py-0.5 text-xs ",
  small: " px-2 py-0.5 text-sm ",
  medium: " px-3 py-1 text-base ",
  large: " px-6 py-2 text-lg "
};

const elevations = {
  none: " shadow-none ",
  low: " shadow-sm ",
  raised: " shadow-md ",
  floating: " shadow-lg ",
  selected: " shadow-xl "
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const {
      usePrimaryColors,
      children,
      size,
      elevation,
      animateSelected,
      action,
      loading,
      className: classNameOverrides,
      innerClassName,
      ...buttonProps
    } = props;

    let className = ` transition-colors duration-200 rounded-md relative active:scale-95 focus:ring-0 focus:ring-offset-0 focus-within:ring-2 ring-0 outline-hidden focus-visible:ring-offset dark:focus-visible:ring-mono-300 `;
    const actionColors = buttonActions[action ?? "primary"];
    if (Object.hasOwn(actionColors, "usePrimaryColors")) {
      className +=
        actionColors[usePrimaryColors ? "usePrimaryColors" : "default"];
    } else {
      className += actionColors["default"];
    }
    className +=
      actionColors[usePrimaryColors ? "usePrimaryColors" : "default"];
    className += sizes[size ?? "large"];
    className += buttonProps.disabled
      ? ` opacity-50 cursor-not-allowed pointer-events-none `
      : ` `;
    className += loading ? ` cursor-wait pointer-events-none ` : ` `;
    className += elevations[elevation ?? "none"];
    className += animateSelected ? ` shadow-selected -transition-y-3 ` : ` `;

    return (
      <button
        className={cn(className, classNameOverrides)}
        {...buttonProps}
        ref={ref}
      >
        <div
          className={cn(
            `transition-opacity ${
              props.loading ? ` opacity-0` : ` opacity-100 `
            }`,
            innerClassName
          )}
        >
          {children}
        </div>
        <div
          className={cn(
            `absolute inset-0  flex items-center justify-center p-1 transition-opacity ${
              props.loading ? ` opacity-100` : ` opacity-0`
            }`
          )}
        >
          <Spinner />
        </div>
      </button>
    );
  }
);

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground dark:text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-button-background shadow-xs dark:text-primary-foreground  hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        default: "h-9 px-4 py-3 md:py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);

import { Slot } from "@radix-ui/react-slot";
import { motion, HTMLMotionProps } from "motion/react";

type ShadButtonBaseProps = VariantProps<typeof buttonVariants> & {
  asChild?: boolean;
  isLoading?: boolean;
  className?: string;
  children?: React.ReactNode;
};

type ShadButtonAsButton = ShadButtonBaseProps &
  Omit<React.ComponentProps<"button">, keyof ShadButtonBaseProps> & {
    as?: "button";
  };

type ShadButtonAsMotion = ShadButtonBaseProps &
  Omit<HTMLMotionProps<"button">, keyof ShadButtonBaseProps> & {
    as: typeof motion.button;
  };

export type ShadButtonProps = ShadButtonAsButton | ShadButtonAsMotion;

export function ShadButton({
  className,
  variant,
  size,
  asChild = false,
  isLoading = false,
  children,
  as,
  ...props
}: ShadButtonProps) {
  const Comp = asChild ? Slot : as === motion.button ? motion.button : "button";
  return (
    <Comp
      data-slot="button"
      {...(props as Record<string, unknown>)}
      className={cn(`relative`, buttonVariants({ variant, size, className }))}
    >
      <span
        className={cn(
          `absolute inset-0 flex items-center justify-center p-1 transition-opacity`,
          isLoading ? "opacity-100" : "opacity-0"
        )}
      >
        <Spinner />
      </span>
      {isLoading ? (
        <div className="pointer-events-none opacity-0">{children}</div>
      ) : (
        children
      )}
    </Comp>
  );
}
